import type { ReportFreshness } from '@/api/types';
import { fmtDateTime } from '@/lib/datetime';

export type ReportingDataset = 'performance' | 'delivery' | 'attribution';

const QUALITY_REASON_LIMIT = 4;

type ProviderAttributionQuality =
  ReportFreshness['provider_attribution_quality'][number];

function readableReason(reason: string): string {
  return reason.replaceAll('_', ' ');
}

function formatConversionMicros(micros: number): string {
  return (micros / 1_000_000).toLocaleString('en-US', {
    maximumFractionDigits: 6,
  });
}

function formatRevenueMicros(micros: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(micros / 1_000_000);
}

function providerQualityNotices(rows: ProviderAttributionQuality[]): string[] {
  if (!rows.length) return [];
  const notices: string[] = [];
  for (const row of rows) {
    const target = row.target.toUpperCase();
    const excludedReasons = [...new Set(row.reasons
      .filter((reason) => reason.disposition === 'excluded')
      .map((reason) => readableReason(reason.reason)))]
      .slice(0, QUALITY_REASON_LIMIT);
    const missingValueReasons = [...new Set(row.reasons
      .filter((reason) => reason.missing_value_entries > 0)
      .map((reason) => readableReason(reason.reason)))]
      .slice(0, QUALITY_REASON_LIMIT);

    if (row.excluded_entries > 0) {
      const measuredImpact = [
        row.excluded_conversions_micros !== 0
          ? `${formatConversionMicros(row.excluded_conversions_micros)} attributed conversions`
          : null,
        row.excluded_revenue_micros !== 0
          ? `${formatRevenueMicros(row.excluded_revenue_micros)} attributed revenue`
          : null,
      ].filter((value): value is string => value !== null);
      notices.push(
        'Provider attribution mapping gaps affected ' +
        row.excluded_entries.toLocaleString('en-US') +
        (row.provider_entries > 0
          ? ' of ' + row.provider_entries.toLocaleString('en-US')
          : '') +
        ' ' + target + ' provider ' +
        (row.excluded_entries === 1 ? 'entry' : 'entries') +
        (row.missing_value_entries > 0
          ? ' (' + row.missing_value_entries.toLocaleString('en-US') +
            ' missing required values)'
          : '') +
        (excludedReasons.length ? ': ' + excludedReasons.join(', ') : '') + '. ' +
        (measuredImpact.length
          ? 'Measured excluded impact: ' + measuredImpact.join(' · ') + '. '
          : '') +
        (row.missing_value_entries > 0
          ? 'Attributed revenue impact is partial or unknown because ' +
            row.missing_value_entries.toLocaleString('en-US') + ' provider ' +
            (row.missing_value_entries === 1 ? 'entry lacked' : 'entries lacked') +
            ' required values. '
          : '') +
        'Excluded attributed conversions and revenue are not shown as zero; visible totals may be lower bounds.',
      );
    } else if (row.missing_value_entries > 0) {
      const affectedEntries = row.provider_entries > 0
        ? row.missing_value_entries.toLocaleString('en-US') + ' of ' +
          row.provider_entries.toLocaleString('en-US') + ' provider entries'
        : row.missing_value_entries.toLocaleString('en-US') + ' provider ' +
          (row.missing_value_entries === 1 ? 'entry' : 'entries');
      notices.push(
        target + ' provider attribution is missing monetary values for ' +
        affectedEntries +
        (missingValueReasons.length ? ': ' + missingValueReasons.join(', ') : '') + '. ' +
        'Attributed revenue is partial or unknown and is not confirmed zero.',
      );
    }

    if (row.deduplicated_entries > 0) {
      notices.push(
        target + ' provider reconciliation collapsed ' +
        row.deduplicated_entries.toLocaleString('en-US') + ' duplicate ' +
        (row.deduplicated_entries === 1 ? 'entry' : 'entries') +
        ' before totals; this is neutral accounting, not lost attribution.',
      );
    }
  }
  return notices;
}

/**
 * Turn canonical publication metadata into plain-language correctness notices.
 * Keeping this logic shared ensures every reporting surface distinguishes
 * unavailable/partial facts from confirmed zeroes.
 */
export function reportingFreshnessNotices(
  freshness: ReportFreshness | undefined,
  dataset: ReportingDataset = 'performance',
): string[] {
  if (!freshness) return [];

  const notices: string[] = [];
  const providerQuality = freshness.provider_attribution_quality ?? [];
  if (dataset !== 'attribution') {
    if (freshness.delivery_is_complete == null) {
      notices.push(
        'No published delivery snapshot covers this scope and date range; delivery metrics are unavailable, not confirmed zero.',
      );
    } else if (!freshness.delivery_is_complete) {
      notices.push(
        `Delivery data is still arriving${
          freshness.delivery_sources?.length
            ? ` (${freshness.delivery_sources.join(', ')})`
            : ''
        }${
          freshness.delivery_as_of
            ? ` · as of ${fmtDateTime(freshness.delivery_as_of)} ET`
            : ''
        }.`,
      );
    }
    if (freshness.delivery_is_stale === true) {
      notices.push(
        'The latest delivery publication is stale' +
        (freshness.delivery_as_of
          ? ' · last data ' + fmtDateTime(freshness.delivery_as_of) + ' ET'
          : '') +
        '; refresh and treat delivery figures as an older snapshot.',
      );
    }
  }

  if (dataset !== 'delivery') {
    if (freshness.attribution_is_complete == null) {
      notices.push(
        'No published Meta/TTD attribution snapshot covers this date range; attributed conversions, revenue, and ROAS are unavailable, not confirmed zero.',
      );
    } else if (!freshness.attribution_is_complete) {
      notices.push(
        `Provider attribution data is incomplete or unsettled for the selected date range${
          freshness.attribution_as_of
            ? ` · as of ${fmtDateTime(freshness.attribution_as_of)} ET`
            : ''
        }; attributed conversion, revenue, and ROAS figures may still change.`,
      );
    }

    if (freshness.attribution_is_stale === true) {
      notices.push(
        'The latest provider attribution publication is stale' +
        (freshness.attribution_as_of
          ? ' · last data ' + fmtDateTime(freshness.attribution_as_of) + ' ET'
          : '') +
        '; attributed conversions, revenue, and ROAS may not include recent provider activity.',
      );
    }

    const legacyWarningReasons = freshness.attribution_warning_reasons ?? [];
    const warningReasons = providerQuality.length
      ? legacyWarningReasons.filter((reason) => reason !== 'provider_conversion_unmapped')
      : legacyWarningReasons;
    if (warningReasons.length) {
      const labels = warningReasons
        .slice(0, QUALITY_REASON_LIMIT)
        .map(readableReason);
      const remaining = warningReasons.length - labels.length;
      notices.push(
        `The published provider snapshot excluded rows for reconciliation reasons (${labels.join(', ')}${
          remaining > 0 ? `, +${remaining} more` : ''
        }); attributed conversion, revenue, and ROAS figures may be lower bounds.`,
      );
    }
    notices.push(...providerQualityNotices(providerQuality));
  }

  return notices;
}

export function ReportingFreshnessWarnings({
  freshness,
  dataset = 'performance',
}: {
  freshness: ReportFreshness | undefined;
  dataset?: ReportingDataset;
}) {
  const notices = reportingFreshnessNotices(freshness, dataset);
  if (!notices.length) return null;

  return (
    <div
      className="mono"
      role="status"
      style={{
        padding: '7px 18px',
        borderBottom: '1px solid var(--line)',
        color: 'var(--warn)',
        fontSize: 10.5,
      }}
    >
      {notices.map((notice) => <div key={notice}>{notice}</div>)}
    </div>
  );
}
